import { BookMarked, ExternalLink, GitBranch, Highlighter, Link2, Plus, Search, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type { EntityLink, NoteCard, OntologyType, ReadAnchor, ReadUrlResolution, RelationType, SourceKind, SourceNode } from "../types";
import { EntityLinksPanel } from "./EntityLinksPanel";
import { OntologyManagerPanel } from "./OntologyManagerPanel";
import { StatusBadge } from "./StatusBadge";
import { useSecondClickConfirm } from "../lib/useSecondClickConfirm";

type Props = {
  sources: SourceNode[];
  notes: NoteCard[];
  linksByObject: Record<string, EntityLink[]>;
  anchorsBySource: Record<string, ReadAnchor[]>;
  ontologyTypes: OntologyType[];
  relationTypes: RelationType[];
  initialSourceId?: string;
  sourceError?: string;
  linkError?: string;
  readError?: string;
  relationError?: string;
  ontologyError?: string;
  onCreateSource: (input: { title: string; kind: SourceKind; properties?: Record<string, unknown> }) => Promise<string | undefined>;
  onUpdateSource: (id: string, input: { properties?: Record<string, unknown> }) => Promise<void>;
  onCreateLink: (objectId: string, input: { url: string; label?: string }) => Promise<void>;
  onDeleteLink: (objectId: string, linkId: string) => Promise<void>;
  onCreateRelation: (input: { sourceObjectId: string; relationType: string; targetObjectId: string }) => Promise<void>;
  onLoadLinks: (objectId: string) => Promise<void>;
  onResolveUrl: (url: string) => Promise<ReadUrlResolution>;
  onLoadAnchors: (sourceId: string) => Promise<void>;
  onCreateAnchor: (
    sourceId: string,
    input: {
      sourceUrl?: string;
      quoteText: string;
      prefix?: string;
      suffix?: string;
      noteTitle?: string;
      noteContent?: string;
    }
  ) => Promise<void>;
  onDeleteAnchor: (sourceId: string, anchorId: string) => Promise<void>;
  onCreateType: (input: { slug: string; name: string; description?: string; parentTypeId?: string | null }) => Promise<void>;
  onUpdateType: (id: string, input: { name?: string; description?: string; parentTypeId?: string | null }) => Promise<void>;
  onCreateRelationType: (input: {
    slug: string;
    name: string;
    description?: string;
    sourceTypeId?: string | null;
    targetTypeId?: string | null;
    inverseSlug?: string | null;
    directional?: boolean;
  }) => Promise<void>;
  onUpdateRelationType: (
    id: string,
    input: {
      name?: string;
      description?: string;
      sourceTypeId?: string | null;
      targetTypeId?: string | null;
      inverseSlug?: string | null;
      directional?: boolean;
    }
  ) => Promise<void>;
  onSelectSource?: (id: string) => void;
};

function hostname(value: string) {
  try {
    return new URL(value).hostname.replace(/^www\./, "");
  } catch {
    return "";
  }
}

function inferMetadata(value: string) {
  try {
    const url = new URL(value.trim());
    const host = url.hostname.toLowerCase().replace(/^www\./, "").replace(/^m\./, "");
    const parts = url.pathname.split("/").filter(Boolean);

    if (host.includes("facebook.com") || host === "fb.watch") {
      return {
        platform: "Facebook",
        author: parts[0] && !["story.php", "permalink.php", "posts", "groups", "share", "watch"].includes(parts[0]) ? parts[0] : ""
      };
    }

    if (host.endsWith("substack.com")) {
      const author = host.replace(".substack.com", "");
      return { platform: "Substack", author: host === "substack.com" && parts[0]?.startsWith("@") ? parts[0] : author === "substack" ? "" : author };
    }

    if (host.includes("medium.com")) {
      return { platform: "Medium", author: parts.find((part) => part.startsWith("@"))?.replace(/^@/, "") ?? "" };
    }

    if (host.includes("linkedin.com")) {
      return { platform: "LinkedIn", author: parts[1] ?? "" };
    }

    if (host.includes("x.com") || host.includes("twitter.com")) {
      return { platform: host.includes("twitter.com") ? "Twitter" : "X", author: parts[0] ?? "" };
    }

    if (host === "youtu.be" || host.endsWith("youtube.com")) {
      const author =
        parts[0]?.startsWith("@")
          ? parts[0]
          : ["channel", "c", "user"].includes(parts[0] ?? "")
            ? parts[1] ?? ""
            : "";
      return { platform: "YouTube", author };
    }

    return { platform: "", author: "" };
  } catch {
    return { platform: "", author: "" };
  }
}

function inferSourceKind(value: string): SourceKind {
  try {
    const host = new URL(value.trim()).hostname.toLowerCase().replace(/^www\./, "").replace(/^m\./, "");
    return host === "youtu.be" || host.endsWith("youtube.com") ? "youtube" : "article";
  } catch {
    return "article";
  }
}

function isYouTubeUrl(value: string) {
  try {
    const host = new URL(value).hostname.toLowerCase().replace(/^www\./, "").replace(/^m\./, "");
    return host === "youtu.be" || host.endsWith("youtube.com");
  } catch {
    return false;
  }
}

function textFragmentUrl(url: string, quote: string) {
  if (!url || !quote.trim()) {
    return url;
  }

  if (isYouTubeUrl(url)) {
    return url;
  }

  const normalized = quote.trim().replace(/\s+/g, " ").slice(0, 180);
  return `${url}#:~:text=${encodeURIComponent(normalized)}`;
}

const sourceKindOptions: Array<{ kind: SourceKind; label: string }> = [
  { kind: "article", label: "Article" },
  { kind: "youtube", label: "YouTube" },
  { kind: "book", label: "Book" },
  { kind: "course", label: "Course" },
  { kind: "topic", label: "Topic" },
  { kind: "output", label: "Output" }
];

export function ReadPanel({
  sources,
  notes,
  linksByObject,
  anchorsBySource,
  ontologyTypes,
  relationTypes,
  initialSourceId,
  sourceError,
  linkError,
  readError,
  relationError,
  ontologyError,
  onCreateSource,
  onCreateLink,
  onDeleteLink,
  onCreateRelation,
  onLoadLinks,
  onResolveUrl,
  onLoadAnchors,
  onCreateAnchor,
  onDeleteAnchor,
  onCreateType,
  onUpdateType,
  onCreateRelationType,
  onUpdateRelationType,
  onSelectSource
}: Props) {
  const [sourceSearch, setSourceSearch] = useState("");
  const readSources = useMemo(() => {
    const query = sourceSearch.trim().toLowerCase();
    return sources.filter((source) => {
      const haystack = [
        source.title,
        source.kind,
        String(source.properties?.platform ?? ""),
        String(source.properties?.author ?? ""),
        String(source.properties?.sourceUrl ?? "")
      ]
        .join(" ")
        .toLowerCase();
      return !query || haystack.includes(query);
    });
  }, [sourceSearch, sources]);
  const [selectedId, setSelectedId] = useState(initialSourceId ?? "");
  const selected = sources.find((source) => source.id === selectedId) ?? readSources[0] ?? sources[0];
  const selectedLinks = selected ? linksByObject[selected.id] ?? [] : [];
  const selectedAnchors = selected ? anchorsBySource[selected.id] ?? [] : [];
  const primaryUrl = selectedLinks[0]?.url || (typeof selected?.properties?.sourceUrl === "string" ? selected.properties.sourceUrl : "");

  // Inputs an output can be "produced_from": notes + non-output sources (not the output itself).
  const inputTargets = useMemo(
    () => [
      ...notes.map((note) => ({ id: note.id, title: note.title, kind: note.kind })),
      ...sources.filter((source) => source.kind !== "output").map((source) => ({ id: source.id, title: source.title, kind: source.kind }))
    ],
    [notes, sources]
  );

  const [articleTitle, setArticleTitle] = useState("");
  const [articleUrl, setArticleUrl] = useState("");
  const [sourceKind, setSourceKind] = useState<SourceKind>("article");
  const [isOutput, setIsOutput] = useState(false);
  const [produceTargetId, setProduceTargetId] = useState("");
  const [isLinkingInput, setIsLinkingInput] = useState(false);
  const [platform, setPlatform] = useState("");
  const [author, setAuthor] = useState("");
  const [urlResolution, setUrlResolution] = useState<ReadUrlResolution | null>(null);
  const [isResolvingUrl, setIsResolvingUrl] = useState(false);
  const [quoteText, setQuoteText] = useState("");
  const [prefix, setPrefix] = useState("");
  const [suffix, setSuffix] = useState("");
  const [noteContent, setNoteContent] = useState("");
  const [activeAnchorId, setActiveAnchorId] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const anchorDeleteConfirm = useSecondClickConfirm();

  useEffect(() => {
    if (initialSourceId && sources.some((source) => source.id === initialSourceId)) {
      setSelectedId(initialSourceId);
    }
  }, [initialSourceId, sources]);

  useEffect(() => {
    if (!selected) {
      return;
    }

    setSelectedId(selected.id);

    if (!linksByObject[selected.id]) {
      void onLoadLinks(selected.id);
    }
    if (!anchorsBySource[selected.id]) {
      void onLoadAnchors(selected.id);
    }
  }, [anchorsBySource, linksByObject, onLoadAnchors, onLoadLinks, selected]);

  useEffect(() => {
    const normalized = articleUrl.trim();
    if (!normalized || !/^https?:\/\//i.test(normalized)) {
      setUrlResolution(null);
      return;
    }

    const inferred = inferMetadata(normalized);
    const inferredKind = inferSourceKind(normalized);
    setPlatform((current) => current || inferred.platform);
    setAuthor((current) => current || inferred.author);
    setSourceKind((current) => (current === "article" && inferredKind !== "article" ? inferredKind : current));

    const timeout = window.setTimeout(() => {
      setIsResolvingUrl(true);
      onResolveUrl(normalized)
        .then((resolution) => {
          setUrlResolution(resolution);
          setPlatform((current) => current || resolution.suggestion.platform);
          setAuthor((current) => current || resolution.suggestion.author);
        })
        .finally(() => setIsResolvingUrl(false));
    }, 350);

    return () => window.clearTimeout(timeout);
  }, [articleUrl]);

  const chooseSource = (sourceId: string) => {
    setSelectedId(sourceId);
    onSelectSource?.(sourceId);
  };

  const selectExistingRead = async (sourceId: string) => {
    chooseSource(sourceId);
    await Promise.all([onLoadLinks(sourceId), onLoadAnchors(sourceId)]);
  };

  const resetArticleForm = () => {
    setArticleTitle("");
    setArticleUrl("");
    setPlatform("");
    setAuthor("");
    setSourceKind("article");
    setIsOutput(false);
    setUrlResolution(null);
  };

  const handleLinkInput = async () => {
    if (!selected || !produceTargetId) {
      return;
    }
    setIsLinkingInput(true);
    try {
      await onCreateRelation({
        sourceObjectId: selected.id,
        relationType: "produced_from",
        targetObjectId: produceTargetId
      });
      setProduceTargetId("");
    } finally {
      setIsLinkingInput(false);
    }
  };

  const handleCreateArticle = async () => {
    const normalizedUrl = articleUrl.trim();
    const normalizedTitle = articleTitle.trim() || hostname(normalizedUrl) || "Untitled read";
    if (!normalizedUrl) {
      return;
    }

    setIsSaving(true);
    try {
      if (urlResolution?.exists && urlResolution.source) {
        await selectExistingRead(urlResolution.source.id);
        resetArticleForm();
        return;
      }

      const createdKind = isOutput ? "output" : sourceKind;
      const createdId = await onCreateSource({
        title: normalizedTitle,
        kind: createdKind,
        properties: {
          platform: platform.trim(),
          author: author.trim(),
          sourceUrl: normalizedUrl
        }
      });

      if (createdId) {
        await onCreateLink(createdId, { url: normalizedUrl, label: platform.trim() || hostname(normalizedUrl) });
        await onLoadLinks(createdId);
        await onLoadAnchors(createdId);
        chooseSource(createdId);
      }

      resetArticleForm();
    } finally {
      setIsSaving(false);
    }
  };

  const handleCreateAnchor = async () => {
    if (!selected || !quoteText.trim()) {
      return;
    }

    setIsSaving(true);
    try {
      await onCreateAnchor(selected.id, {
        sourceUrl: primaryUrl || undefined,
        quoteText: quoteText.trim(),
        prefix: prefix.trim(),
        suffix: suffix.trim(),
        noteTitle: quoteText.trim().slice(0, 120),
        noteContent: noteContent.trim() || `> ${quoteText.trim()}`
      });
      await onLoadAnchors(selected.id);
      setQuoteText("");
      setPrefix("");
      setSuffix("");
      setNoteContent("");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <section className="rounded-lg border border-line bg-white shadow-panel">
        <div className="border-b border-line px-4 py-4">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="mb-2 inline-flex h-8 items-center gap-2 rounded-md border border-blue-100 bg-blue-50 px-3 text-xs font-bold text-brand">
                <BookMarked size={15} aria-hidden="true" />
                Link
              </div>
              <h1 className="text-xl font-bold leading-tight text-ink">Sources, links, quotes, and notes</h1>
              <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
                Gan link cho bat ky source nao, luu quote, viet note, va mo lai nguon goc khi can doc tiep.
              </p>
            </div>
            <span className="rounded-md border border-line bg-slate-50 px-2 py-1 text-xs font-bold text-muted">
              {readSources.length} sources
            </span>
          </div>
        </div>

        <div className="grid gap-4 p-4 xl:grid-cols-[300px_minmax(0,1fr)_380px]">
          <aside className="space-y-3">
            <div className="rounded-lg border border-line bg-slate-50 p-3">
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-muted">
                <Plus size={14} aria-hidden="true" />
                Them link / source
              </div>
              <div className="mt-3 space-y-2">
                <input
                  className="h-9 w-full rounded-lg border border-line bg-white px-3 text-sm text-ink"
                  value={articleUrl}
                  onChange={(event) => setArticleUrl(event.target.value)}
                  placeholder="https://..."
                  aria-label="Source URL"
                />
                {urlResolution?.exists && urlResolution.source ? (
                  <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                    <p className="text-xs font-bold uppercase tracking-wide text-brand">Link da co trong he thong</p>
                    <button
                      className="mt-2 w-full text-left text-sm font-bold text-ink hover:text-brand"
                      type="button"
                      onClick={() => void selectExistingRead(urlResolution.source!.id)}
                    >
                      {urlResolution.source.title}
                    </button>
                    <p className="mt-1 text-xs font-semibold text-muted">
                      {[
                        urlResolution.source.platform || urlResolution.suggestion.platform,
                        urlResolution.source.author || urlResolution.suggestion.author
                      ]
                        .filter(Boolean)
                        .join(" · ") || urlResolution.normalizedUrl}
                    </p>
                  </div>
                ) : isResolvingUrl ? (
                  <p className="text-xs font-semibold text-muted">Dang kiem tra link da luu...</p>
                ) : null}
                <input
                  className="h-9 w-full rounded-lg border border-line bg-white px-3 text-sm text-ink"
                  value={articleTitle}
                  onChange={(event) => setArticleTitle(event.target.value)}
                  placeholder="Tiêu đề"
                  aria-label="Source title"
                />
                <select
                  className="h-9 w-full rounded-lg border border-line bg-white px-3 text-sm text-ink"
                  value={sourceKind}
                  onChange={(event) => setSourceKind(event.target.value as SourceKind)}
                  aria-label="Source kind"
                  disabled={isOutput}
                >
                  {sourceKindOptions.map((option) => (
                    <option key={option.kind} value={option.kind}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    className="h-9 min-w-0 rounded-lg border border-line bg-white px-3 text-sm text-ink"
                    value={platform}
                    onChange={(event) => setPlatform(event.target.value)}
                    placeholder="YouTube, Substack..."
                    aria-label="Source platform"
                  />
                  <input
                    className="h-9 min-w-0 rounded-lg border border-line bg-white px-3 text-sm text-ink"
                    value={author}
                    onChange={(event) => setAuthor(event.target.value)}
                    placeholder="Tac gia/kenh"
                    aria-label="Source author"
                  />
                </div>
                <label className="flex items-start gap-2 rounded-lg border border-line bg-white px-3 py-2 text-xs font-semibold text-slate-700">
                  <input
                    className="mt-0.5"
                    type="checkbox"
                    checked={isOutput}
                    onChange={(event) => setIsOutput(event.target.checked)}
                  />
                  <span>
                    Đây là <span className="font-bold text-purple-700">output của tôi</span> (bài tôi đã đăng) — không phải nguồn tôi đọc.
                  </span>
                </label>
                <button
                  className="inline-flex h-9 w-full items-center justify-center gap-2 rounded-lg bg-brand px-3 text-sm font-bold text-white hover:bg-blue-700 disabled:bg-slate-300"
                  type="button"
                  disabled={!articleUrl.trim() || isSaving}
                  onClick={() => void handleCreateArticle()}
                >
                  <Plus size={16} aria-hidden="true" />
                  {urlResolution?.exists ? "Dùng bài đã có" : isOutput ? "Lưu output" : "Lưu bài đọc"}
                </button>
                {sourceError || linkError ? (
                  <p className="text-xs font-semibold text-red-700">{sourceError || linkError}</p>
                ) : null}
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex h-9 items-center gap-2 rounded-lg border border-line bg-white px-3 text-muted">
                <Search size={15} aria-hidden="true" />
                <input
                  className="h-full min-w-0 flex-1 border-0 bg-transparent text-sm font-semibold text-ink outline-none placeholder:text-slate-400"
                  value={sourceSearch}
                  onChange={(event) => setSourceSearch(event.target.value)}
                  placeholder="Tim source, link, nen tang..."
                  aria-label="Search sources"
                />
              </div>
              {readSources.length === 0 ? (
                <p className="rounded-lg border border-dashed border-line bg-white px-3 py-4 text-sm font-semibold text-muted">
                  Chua co source phu hop.
                </p>
              ) : (
                readSources.map((source) => (
                  <button
                    key={source.id}
                    className={`w-full rounded-lg border p-3 text-left transition ${
                      selected?.id === source.id ? "border-blue-200 bg-blue-50" : "border-line bg-white hover:bg-slate-50"
                    }`}
                    type="button"
                    onClick={() => chooseSource(source.id)}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <h3 className="truncate text-sm font-bold">{source.title}</h3>
                        <p className="mt-1 truncate text-xs font-semibold text-muted">
                          {[
                            String(source.properties?.platform ?? "") || hostname(String(source.properties?.sourceUrl ?? "")),
                            String(source.properties?.author ?? "")
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                        </p>
                      </div>
                      <StatusBadge kind={source.kind} />
                    </div>
                    <p className="mt-2 text-xs font-semibold text-muted">{source.noteCount} notes</p>
                  </button>
                ))
              )}
            </div>
          </aside>

          <main className="min-w-0 rounded-lg border border-line bg-white">
            <div className="border-b border-line px-4 py-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <h2 className="truncate text-base font-bold">{selected?.title ?? "Chọn bài đọc"}</h2>
                  {primaryUrl ? <p className="mt-1 truncate text-xs font-semibold text-muted">{primaryUrl}</p> : null}
                </div>
                {primaryUrl ? (
                  <a
                    className="inline-flex h-9 items-center gap-2 rounded-lg border border-line bg-white px-3 text-sm font-bold text-slate-700 hover:bg-slate-50"
                    href={textFragmentUrl(primaryUrl, selectedAnchors[0]?.quoteText ?? "")}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <ExternalLink size={15} aria-hidden="true" />
                    Mo bai goc
                  </a>
                ) : null}
              </div>
            </div>

            <div className="space-y-4 p-4">
              {selected ? (
                <EntityLinksPanel
                  objectId={selected.id}
                  links={selectedLinks}
                  error={linkError}
                  onLoad={onLoadLinks}
                  onCreate={onCreateLink}
                  onDelete={onDeleteLink}
                />
              ) : null}

              {selected?.kind === "output" ? (
                <div className="rounded-lg border border-purple-200 bg-purple-50 p-3">
                  <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-purple-700">
                    <GitBranch size={14} aria-hidden="true" />
                    Nối input đã tạo ra output này
                  </div>
                  <div className="mt-3 grid gap-2 sm:grid-cols-[minmax(0,1fr)_auto]">
                    <select
                      className="h-9 min-w-0 rounded-lg border border-line bg-white px-3 text-sm text-ink"
                      value={produceTargetId}
                      onChange={(event) => setProduceTargetId(event.target.value)}
                      aria-label="Chọn input"
                    >
                      <option value="">Chọn note/nguồn đầu vào...</option>
                      {inputTargets.map((target) => (
                        <option key={target.id} value={target.id}>
                          [{target.kind}] {target.title}
                        </option>
                      ))}
                    </select>
                    <button
                      className="inline-flex h-9 items-center justify-center gap-2 rounded-lg bg-purple-600 px-3 text-sm font-bold text-white hover:bg-purple-700 disabled:bg-slate-300"
                      type="button"
                      disabled={!produceTargetId || isLinkingInput}
                      onClick={() => void handleLinkInput()}
                    >
                      <Plus size={15} aria-hidden="true" />
                      {isLinkingInput ? "Đang nối" : "Nối input"}
                    </button>
                  </div>
                  {relationError ? <p className="mt-2 text-xs font-semibold text-red-700">{relationError}</p> : null}
                  <p className="mt-2 text-[11px] text-purple-700/80">Mỗi input nối vào sẽ thành cạnh input → output trong graph.</p>
                </div>
              ) : null}

              <div className="rounded-lg border border-blue-100 bg-blue-50 p-3">
                <div className="flex items-start gap-3">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-blue-200 bg-white text-brand">
                    <Highlighter size={16} aria-hidden="true" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-sm font-bold">Lưu quote, không cần copy cả bài</h3>
                    <p className="mt-1 text-sm leading-6 text-slate-600">
                      Mỗi highlight sẽ giữ quote, note và link mở đoạn gốc bằng text fragment. Nếu site không hỗ trợ, link vẫn mở bài gốc.
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid gap-3 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-wide text-muted" htmlFor="read-quote">
                    Đoạn highlight
                  </label>
                  <textarea
                    id="read-quote"
                    className="min-h-40 w-full resize-y rounded-lg border border-line bg-slate-50 p-3 text-sm leading-6 text-ink"
                    value={quoteText}
                    disabled={!selected}
                    onChange={(event) => setQuoteText(event.target.value)}
                    placeholder="Copy đoạn quan trọng trong bài gốc rồi paste vào đây."
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      className="h-9 min-w-0 rounded-lg border border-line bg-white px-3 text-sm text-ink"
                      value={prefix}
                      disabled={!selected}
                      onChange={(event) => setPrefix(event.target.value)}
                      placeholder="Ngữ cảnh trước"
                      aria-label="Quote prefix"
                    />
                    <input
                      className="h-9 min-w-0 rounded-lg border border-line bg-white px-3 text-sm text-ink"
                      value={suffix}
                      disabled={!selected}
                      onChange={(event) => setSuffix(event.target.value)}
                      placeholder="Ngữ cảnh sau"
                      aria-label="Quote suffix"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-wide text-muted" htmlFor="read-note">
                    Note của bạn
                  </label>
                  <textarea
                    id="read-note"
                    className="min-h-40 w-full resize-y rounded-lg border border-line bg-white p-3 text-sm leading-6 text-ink"
                    value={noteContent}
                    disabled={!selected}
                    onChange={(event) => setNoteContent(event.target.value)}
                    placeholder="Viết ý của bạn về đoạn này. Để trống thì Human Note tạo quote note từ đoạn highlight."
                  />
                  <button
                    className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-lg bg-brand px-4 text-sm font-bold text-white hover:bg-blue-700 disabled:bg-slate-300"
                    type="button"
                    disabled={!selected || !quoteText.trim() || isSaving}
                    onClick={() => void handleCreateAnchor()}
                  >
                    <Link2 size={16} aria-hidden="true" />
                    Lưu quote + note
                  </button>
                  {readError ? <p className="text-xs font-semibold text-red-700">{readError}</p> : null}
                </div>
              </div>
            </div>
          </main>

          <aside className="rounded-lg border border-line bg-white">
            <div className="border-b border-line px-3 py-2">
              <h3 className="text-sm font-bold">Quote cards</h3>
            </div>
            <div className="max-h-[360px] space-y-2 overflow-y-auto p-3 md:max-h-[640px]">
              {selectedAnchors.length === 0 ? (
                <p className="rounded-lg border border-dashed border-line bg-slate-50 px-3 py-4 text-sm font-semibold text-muted">
                  Chưa có quote nào cho bài này.
                </p>
              ) : (
                selectedAnchors.map((anchor) => (
                  <article
                    key={anchor.id}
                    className={`rounded-lg border p-3 ${
                      activeAnchorId === anchor.id ? "border-yellow-300 bg-yellow-50" : "border-line bg-white"
                    }`}
                  >
                    <button className="w-full text-left" type="button" onClick={() => setActiveAnchorId(anchor.id)}>
                      <p className="text-sm leading-6 text-slate-700">"{anchor.quoteText}"</p>
                      {anchor.noteTitle ? <p className="mt-2 text-xs font-semibold text-muted">{anchor.noteTitle}</p> : null}
                    </button>
                    <div className="mt-3 flex items-center justify-between gap-2">
                      {primaryUrl ? (
                        <a
                          className="inline-flex h-8 items-center gap-2 rounded-lg border border-line bg-white px-2 text-xs font-bold text-slate-700 hover:bg-slate-50"
                          href={textFragmentUrl(primaryUrl, anchor.quoteText)}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <ExternalLink size={13} aria-hidden="true" />
                          Mo doan goc
                        </a>
                      ) : (
                        <span />
                      )}
                      <button
                        className="inline-flex h-8 items-center gap-2 rounded-lg border border-red-200 bg-white px-2 text-xs font-bold text-red-700 hover:bg-red-50"
                        type="button"
                        onClick={() => selected && anchorDeleteConfirm.request(anchor.id, () => void onDeleteAnchor(selected.id, anchor.id))}
                      >
                        <Trash2 size={13} aria-hidden="true" />
                        {anchorDeleteConfirm.pendingId === anchor.id ? "Bấm lần nữa" : "Xóa"}
                      </button>
                    </div>
                  </article>
                ))
              )}
            </div>
          </aside>
        </div>
      </section>

      <OntologyManagerPanel
        types={ontologyTypes}
        relationTypes={relationTypes}
        onCreateType={onCreateType}
        onUpdateType={onUpdateType}
        onCreateRelationType={onCreateRelationType}
        onUpdateRelationType={onUpdateRelationType}
        error={ontologyError}
      />
    </div>
  );
}
